/**
 * Directory entity media atom — md 56×56 (compact Home nearby / dense lists; D23 52–60),
 * lg 64×64 (Retail V1 pickers), xl 80×80 (reserved larger marks). Parent owns cascade
 * via `candidates` (first entry wins; empty → monogram). Chip only when
 * `showTenantChip` + `tenantChipSrc`.
 *
 * Brand-kit rim uses `--logo-chip-rim` (G10) — never `--directory-thumb-chip-ring`
 * (Obchody storePhoto overlay chip ring).
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  buildLogoChipMarkStyle,
  resolveConsumerSquareLogoForTheme,
  resolveLogoChipBackgroundForTheme,
  resolveLogoChipRimForTheme,
  type ConsumerSquareLogoSettings,
  type LogoChipBackgroundSettings,
  type LogoChipRimSettings,
  type LogoChipRimTheme,
} from 'pi-kiosk-shared';
import type { DirectoryMediaCandidate, DirectoryMediaProfile } from './directoryCascade.js';
import { resolveDirectoryMonogram, type DirectoryMonogramInput } from './directoryMonogram.js';

/** `md` = Home nearby / dense lists (56px); `lg` = Obchody / Organizace pickers; `xl` = larger marks. */
export type DirectoryEntityThumbSize = 'md' | 'lg' | 'xl';

export interface DirectoryEntityThumbProps {
  /**
   * Parent-owned ordered cascade. First entry is painted; empty → monogram.
   * On image error, parent should drop the failed src and re-pass candidates.
   */
  readonly candidates: readonly DirectoryMediaCandidate[];
  /** Entity identity for monogram label + WCAG AA colors. */
  readonly monogram: DirectoryMonogramInput;
  /**
   * When true and `tenantChipSrc` is set, paint decorative tenant logo chip.
   * Platform Obchody only — parents pass false for Oblíbené / tenant-scoped / orgs.
   */
  readonly showTenantChip?: boolean;
  readonly tenantChipSrc?: string | null;
  /** Parent advances cascade (drop failed candidate). */
  readonly onImageError?: (failedSrc: string) => void;
  /** Parent clears chip src on failure. */
  readonly onChipError?: (failedSrc: string) => void;
  /**
   * Eager-first-6: first 6 visible Obchody/Organizace rows pass `eager` + `high`.
   * Default lazy for below-fold / Oblíbené / memberships when parent omits.
   */
  readonly loading?: 'eager' | 'lazy';
  readonly fetchPriority?: 'high' | 'low' | 'auto';
  /** Default `md` (56px, D23 band). Picker surfaces pass `lg` (64px). */
  readonly size?: DirectoryEntityThumbSize;
  /**
   * When true, media uses `object-cover` with no inset pad so the image fills the box
   * (legacy stack cards). Default keeps brandMark contain + transparent pad / storePhoto cover.
   */
  readonly fillBox?: boolean;
  /**
   * Dual-regime brand-kit rim settings (public tenant row). Resolved against
   * `logoChipRimTheme` via `resolveLogoChipRimForTheme`.
   */
  readonly logoChipRim?: LogoChipRimSettings | null;
  /**
   * Dual-regime brand-kit background for brandMark transparent pad.
   * Resolved against `logoChipRimTheme` via `resolveLogoChipBackgroundForTheme`.
   */
  readonly logoChipBackground?: LogoChipBackgroundSettings | null;
  /** Active light|dark theme for rim/background/URL visibility. Default `light`. */
  readonly logoChipRimTheme?: LogoChipRimTheme;
  /**
   * When set and active profile is brandMark, re-resolve src via
   * `resolveConsumerSquareLogoForTheme` (theme switch without stale cascade src).
   */
  readonly brandMarkLogo?: ConsumerSquareLogoSettings | null;
  readonly className?: string;
  readonly testId?: string;
}

function thumbSizeClass(size: DirectoryEntityThumbSize): string {
  if (size === 'xl') {
    return 'h-[var(--directory-thumb-size-xl)] w-[var(--directory-thumb-size-xl)] rounded-[var(--directory-thumb-radius-xl)]';
  }
  if (size === 'lg') {
    return 'h-[var(--directory-thumb-size-lg)] w-[var(--directory-thumb-size-lg)] rounded-[var(--directory-thumb-radius-lg)]';
  }
  return 'h-[var(--directory-thumb-size)] w-[var(--directory-thumb-size)] rounded-[var(--directory-thumb-radius)]';
}

function MonogramFace(props: {
  readonly text: string;
  readonly backgroundColor: string;
  readonly color: string;
  readonly size: DirectoryEntityThumbSize;
}): ReactNode {
  const style: CSSProperties = {
    backgroundColor: props.backgroundColor,
    color: props.color,
  };
  let textClass = 'text-sm font-semibold uppercase tracking-tight';
  if (props.size === 'xl') {
    textClass = 'text-xl font-semibold uppercase tracking-tight';
  } else if (props.size === 'lg') {
    textClass = 'text-lg font-semibold uppercase tracking-tight';
  }

  return (
    <span
      className={['flex h-full w-full items-center justify-center', textClass].join(' ')}
      style={style}
      aria-hidden="true"
    >
      {props.text}
    </span>
  );
}

function resolveActive(
  candidates: readonly DirectoryMediaCandidate[],
): { profile: DirectoryMediaProfile; src: string | null } {
  const first = candidates[0];
  if (first === undefined) {
    return { profile: 'monogram', src: null };
  }
  return { profile: first.profile, src: first.src };
}

function brandPadClassForSize(size: DirectoryEntityThumbSize): string {
  if (size === 'xl') {
    return 'p-2';
  }
  if (size === 'lg') {
    return 'p-1.5';
  }
  return 'p-0.5';
}

function chipBoxClass(size: DirectoryEntityThumbSize): string {
  if (size === 'xl') {
    return 'h-[var(--directory-thumb-chip-size-xl)] w-[var(--directory-thumb-chip-size-xl)] rounded-[var(--directory-thumb-chip-radius-xl)]';
  }
  if (size === 'lg') {
    return 'h-[var(--directory-thumb-chip-size-lg)] w-[var(--directory-thumb-chip-size-lg)] rounded-[var(--directory-thumb-chip-radius-lg)]';
  }
  return 'h-[var(--directory-thumb-chip-size)] w-[var(--directory-thumb-chip-size)] rounded-[var(--directory-thumb-chip-radius)]';
}

function resolveBrandKitChipStyle(
  rimSettings: LogoChipRimSettings | null | undefined,
  backgroundSettings: LogoChipBackgroundSettings | null | undefined,
  theme: LogoChipRimTheme,
  includeBackground: boolean,
): {
  readonly rim: { readonly show: boolean; readonly style: CSSProperties | undefined };
  readonly background: { readonly show: boolean; readonly style: CSSProperties | undefined };
} {
  const rimResolved =
    rimSettings != null
      ? resolveLogoChipRimForTheme(rimSettings, theme)
      : { show: false, color: '' };
  const backgroundResolved =
    includeBackground && backgroundSettings != null
      ? resolveLogoChipBackgroundForTheme(backgroundSettings, theme)
      : { show: false, color: '' };
  const mergedStyle = buildLogoChipMarkStyle({
    rim: rimResolved,
    background: backgroundResolved,
  }) as CSSProperties | undefined;
  return {
    rim: { show: rimResolved.show, style: mergedStyle },
    background: { show: backgroundResolved.show, style: mergedStyle },
  };
}

/**
 * Decorative directory thumb. Name / a11y live on the row title control.
 * Media is at the logical start of the row (DOM order + flex-row).
 */
export function DirectoryEntityThumb(props: DirectoryEntityThumbProps): JSX.Element {
  const {
    candidates,
    monogram: monogramInput,
    showTenantChip = false,
    tenantChipSrc = null,
    onImageError,
    onChipError,
    loading = 'lazy',
    fetchPriority,
    size = 'md',
    fillBox = false,
    logoChipRim = null,
    logoChipBackground = null,
    logoChipRimTheme = 'light',
    brandMarkLogo = null,
    className,
    testId = 'directory-entity-thumb',
  } = props;

  const chipSrcKey =
    typeof tenantChipSrc === 'string' && tenantChipSrc.trim().length > 0
      ? tenantChipSrc.trim()
      : null;
  /** Hide only for the URL that failed; a new HMAC/list URL clears automatically. */
  const [failedChipSrc, setFailedChipSrc] = useState<string | null>(null);
  const chipHidden = failedChipSrc !== null && failedChipSrc === chipSrcKey;
  const active = resolveActive(candidates);
  const monogram = resolveDirectoryMonogram(monogramInput);
  const brandKitChip = resolveBrandKitChipStyle(
    logoChipRim,
    logoChipBackground,
    logoChipRimTheme,
    active.profile === 'brandMark' || active.profile === 'storePhoto',
  );
  const rim = brandKitChip.rim;
  const background = brandKitChip.background;
  const usesChipBackground =
    (active.profile === 'brandMark' || active.profile === 'storePhoto') && background.show;
  const brandMarkSrc =
    active.profile === 'brandMark' && brandMarkLogo !== null
      ? resolveConsumerSquareLogoForTheme(brandMarkLogo, logoChipRimTheme)
      : active.src;
  const showImage = active.profile !== 'monogram' && brandMarkSrc !== null;
  const activeSrc = showImage ? brandMarkSrc : null;
  let objectFitClass = 'object-contain';
  let brandPadClass = '';
  /** brandMark / storePhoto+chip bg: transparent pad so alpha shows chip fill (brandMark parity). */
  let boxBgClass = 'bg-[var(--directory-thumb-matte)]';
  if (fillBox || (active.profile === 'storePhoto' && !usesChipBackground)) {
    objectFitClass = 'object-cover';
  } else if (
    active.profile === 'brandMark' ||
    (active.profile === 'storePhoto' && usesChipBackground)
  ) {
    brandPadClass = brandPadClassForSize(size);
    boxBgClass = 'bg-transparent';
  }
  const chipUrl =
    showTenantChip &&
    !chipHidden &&
    active.profile === 'storePhoto' &&
    typeof tenantChipSrc === 'string' &&
    tenantChipSrc.trim().length > 0
      ? tenantChipSrc.trim()
      : null;
  const chipBackgroundResolved =
    logoChipBackground != null
      ? resolveLogoChipBackgroundForTheme(logoChipBackground, logoChipRimTheme)
      : { show: false, color: '' };
  const chipOverlayStyle: CSSProperties | undefined = chipBackgroundResolved.show
    ? ({ '--logo-chip-background': chipBackgroundResolved.color } as CSSProperties)
    : undefined;
  const chipOverlayBgClass = chipBackgroundResolved.show
    ? 'bg-[var(--logo-chip-background)]'
    : 'bg-[var(--directory-thumb-matte)]';

  const thumbStyle = rim.style;

  return (
    <span
      className={[
        'relative inline-flex shrink-0 overflow-hidden box-border',
        thumbSizeClass(size),
        usesChipBackground ? '' : boxBgClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={thumbStyle}
      data-testid={testId}
      data-size={size}
      data-profile={active.profile}
      data-logo-chip-rim={rim.show ? 'true' : 'false'}
      data-logo-chip-background={background.show ? 'true' : 'false'}
      aria-hidden="true"
    >
      {showImage && activeSrc !== null ? (
        <img
          src={activeSrc}
          alt=""
          draggable={false}
          loading={loading}
          decoding="async"
          {...(fetchPriority !== undefined ? { fetchPriority } : {})}
          className={['h-full w-full', objectFitClass, brandPadClass].filter(Boolean).join(' ')}
          onError={() => {
            onImageError?.(activeSrc);
          }}
        />
      ) : (
        <MonogramFace
          text={monogram.label}
          backgroundColor={monogram.backgroundColor}
          color={monogram.textColor}
          size={size}
        />
      )}

      {chipUrl !== null ? (
        <span
          className={[
            'pointer-events-none absolute bottom-0 end-0 overflow-hidden',
            chipBoxClass(size),
            chipOverlayBgClass,
            'ring-2 ring-[var(--directory-thumb-chip-ring)]',
          ].join(' ')}
          style={chipOverlayStyle}
          data-testid={`${testId}-chip`}
          data-logo-chip-background={chipBackgroundResolved.show ? 'true' : 'false'}
          aria-hidden="true"
        >
          <img
            src={chipUrl}
            alt=""
            draggable={false}
            className="h-full w-full object-contain p-px"
            onError={() => {
              setFailedChipSrc(chipUrl);
              onChipError?.(chipUrl);
            }}
          />
        </span>
      ) : null}
    </span>
  );
}
